//The UserAdminService is a self-contained provider for the authorization
//management endpoints (roles + supplier scope) exposed under /authz on the v2
//api. Every call requires the caller to hold the user:manage permission, which
//the authorization service re-checks server-side.
import { ApiService } from "./ApiService";

const certblockAPI = new ApiService();

/**
 * Abstraction over the /authz management endpoints used by the user manager
 * screen.
 */
export default class UserAdminService {

	/**
	 * Returns the effective roles of a user.
	 * @param userId
	 * @return {Promise<{userId: String, roles: Array<String>}>}
	 */
	async getRoles(userId) {
		return (await certblockAPI.callApi('userRoles', [userId], 'get')).data;
	}

	/**
	 * Sets a user's explicit roles.
	 * @param userId
	 * @param roles
	 * @return {Promise<Object>}
	 */
	async setRoles(userId, roles) {
		return (await certblockAPI.callApi('userRoles', [userId], 'put', { roles })).data;
	}

	/**
	 * Clears a user's explicit roles, falling back to the legacy role.
	 * @param userId
	 * @return {Promise<void>}
	 */
	async clearRoles(userId) {
		await certblockAPI.callApi('userRoles', [userId], 'delete');
	}

	/**
	 * Returns a user's supplier scope: the primary supplier (read only), the
	 * whitelist and the all-suppliers grant.
	 * @param userId
	 * @return {Promise<{userId: String, primarySupplier: String, supplierIds: Array<String>, supplierWildcard: Boolean}>}
	 */
	async getSuppliers(userId) {
		return (await certblockAPI.callApi('userSuppliers', [userId], 'get')).data;
	}

	/**
	 * Sets a user's supplier whitelist and all-suppliers grant.
	 * @param userId
	 * @param supplierIds
	 * @param supplierWildcard
	 * @return {Promise<Object>}
	 */
	async setSuppliers(userId, supplierIds, supplierWildcard) {
		return (await certblockAPI.callApi('userSuppliers', [userId], 'put', { supplierIds, supplierWildcard })).data;
	}

	/**
	 * Clears a user's supplier whitelist and all-suppliers grant.
	 * @param userId
	 * @return {Promise<void>}
	 */
	async clearSuppliers(userId) {
		await certblockAPI.callApi('userSuppliers', [userId], 'delete');
	}

	/**
	 * Lists all explicit role/supplier assignments.
	 * @return {Promise<{assignments: Array<Object>}>}
	 */
	async listAssignments() {
		return (await certblockAPI.callApi('assignments', [], 'get')).data;
	}
}
